// La marque TerObs — symbole + mot-symbole, dans l'assemblage arrêté par Cyrille le
// 03/08/2026 (docs/marque/DECISION-logo-terobs-20260803.md, planche de contexte
// docs/marque/TerObs-logo-en-contexte.pdf). Quatre règles, toutes vérifiables :
//
//   1. le mot s'écrit « TerObs » en CASSE MIXTE — jamais « TEROBS », jamais « Terobs » ;
//   2. la police est IBM Plex Sans 600 — celle de l'interface, donc aucune substitution
//      au chargement (auto-hébergée par @fontsource ; la CSP du site interdit Google
//      Fonts, d'où l'import ci-dessous plutôt qu'un <link> vers fonts.googleapis.com) ;
//   3. UNE SEULE couleur, la même sur le symbole et sur le mot. C'est pour cela que
//      l'encre vient de `currentColor` : elle se pose une fois sur le conteneur et
//      descend dans le tracé comme dans le texte. Recolorer une lettre ou une barre
//      casse la décision du 03/08 — le bicolore Ter/Obs, la bipolice et le vert y ont
//      été explicitement écartés ;
//   4. deux usages, pas trois : marine #0D1F3C sur fond clair (défaut), blanc plein sur
//      fond marine (`variant="inverse"`). Les deux tiennent 16,3:1 de contraste.
//
// ── Le symbole ────────────────────────────────────────────────────────────────────────
// L'hexagone-escalier, piste A de public/labo/logo-terobs.html, acquise par Marc et
// Cyrille le 29/07. Le contour dit le territoire, les trois barres croissantes disent
// l'historisation. La géométrie n'est pas redessinée à l'œil : elle est relevée sur les
// tracés vectoriels de la planche du 03/08 (`page.get_drawings()`), qui donne un hexagone
// RÉGULIER de rayon 18 centré en (24, 24), un trait de 2,4 et des barres à
// x = 15,6 / 21,9 / 28,2 — au centième près les valeurs de la piste A. Une seule chose
// change par rapport à la planche du 29/07 : elle peignait les barres en #0047b3
// (l'accent du site), c'est-à-dire exactement le bicolore refusé le 03/08 ; elles sont
// ici dans l'encre du contour.
//
// ── Les proportions du lockup ─────────────────────────────────────────────────────────
// Relevées sur la même planche, mot en IBMPlexSans-SemiBold 28,5 pt, puis recoupées au
// pixel sur un rendu 1200 dpi. Rapportées au corps du mot (1 em) :
//   · dessin du symbole      0,9685 em de large × 0,8473 em de haut ;
//   · blanc entre le dessin et l'encre du « T »   0,2807 em — dont 0,026 em d'approche
//     gauche du T, d'où un `gap` CSS de 0,2547 em ;
//   · pied du symbole        0,055 em SOUS la ligne de base du mot.
// Le `viewBox` est resserré sur les limites réelles du tracé (4,8 → 43,2 et 7,2 → 40,8,
// trait compris) pour que la boîte CSS SOIT le dessin : c'est ce qui rend les trois
// mesures ci-dessus directement applicables. Avec `items-center` et un `leading-none`
// sur le mot, le pied du symbole tombe à 0,006 em de sa cible — sous le dixième de pixel
// à 20 px, donc aucune correction verticale n'est nécessaire.
// Tout est en `em` pour que le rapport tienne à toutes les tailles ; ne pas réécrire en px.
//
// ── Planchers de taille (planche, section 01) ─────────────────────────────────────────
// 16 px pour le mot en texte/UI, 24 px pour le symbole employé SEUL (16 px toléré pour le
// seul favicon). `size="sm"` rend le mot à 18 px : on reste au-dessus du plancher.
//
// ── La marque respire (demande Cyrille, 18/09/2026) ───────────────────────────────────
// « Juste pour le fun, ce serait cool qu'on anime le logo de TerObs comme on l'avait fait
// pour le homard de LObsTer. J'ai pensé que les trois barres pourraient changer de couleur
// et de hauteur de temps en temps pour symboliser l'actualisation et la variété des
// données de TerObs. »
//
// ⚠️ CETTE DEMANDE TOUCHE LA RÈGLE 3 CI-DESSUS, et c'est volontaire. Le 03/08, la revue a
// passé en examen « vert sauge sur les barres » et l'a écartée avec toutes les pistes
// bicolores (DECISION-logo-terobs-20260803.md, § pistes écartées). Recolorer les barres,
// même une seconde, revient donc sur un point que Cyrille avait lui-même tranché — il en a
// le droit, c'est sa marque, mais l'écart doit être VISIBLE ici plutôt que découvert plus
// tard. Trois précautions, qui sont la raison pour laquelle l'écart reste tenable :
//
//   a. l'ÉTAT DE RÉFÉRENCE ne bouge pas. Au repos — soit ~7 s sur 9, plus toute impression,
//      tout PDF, tout écran en `prefers-reduced-motion` — le logo est exactement le
//      monochrome du 03/08. L'animation est une parenthèse, jamais l'état du logo ;
//   b. les teintes restent dans la RAMPE MARINE de la palette existante (brand-500/600/700,
//      index.css). Aucune couleur inventée, et surtout aucune SECONDE TEINTE : ni le vert
//      écarté, ni l'accent #0047b3 — qui est précisément le bicolore refusé le 03/08.
//      Ce qu'on lit est une encre qui s'éclaircit, pas un logo bicolore ;
//   c. rien n'est écrit en dur dans le SVG : les barres gardent `fill="currentColor"`, et
//      c'est la propriété `color` qui est animée depuis index.css. La règle « une seule
//      encre, héritée » tient donc au niveau du tracé, y compris pendant l'animation.
//
// ── Une vague, pas une boucle (revue d'accessibilité du 18/09) ────────────────────────
// La première version bouclait toutes les 9 s. Écartée : RGAA 13.8 / WCAG 2.2.2 (« Pause,
// Stop, Hide ») interdit un mouvement qui démarre seul, dure plus de 5 s et n'offre aucun
// moyen de le mettre en pause. `prefers-reduced-motion` ne suffit pas à s'en exonérer — il
// ne couvre que les gens qui ont réglé leur système, pas les autres — et nos clients sont
// des collectivités soumises au RGAA.
//
// D'où le partage actuel : UNE vague au montage (2,59 s, sous le seuil des 5 s), puis plus
// rien d'automatique ; et un rejeu au SURVOL ou au FOCUS, déclenché par l'utilisateur, donc
// hors du champ du critère. Aucun bouton ajouté, aucune commande à l'écran.
//
// La chorégraphie elle-même vit dans index.css (bloc « La marque respire »). Ici, on
// pose les crochets — `logo-anime` sur le SVG, `logo-barre-1/2/3` sur les barres — et on
// gère le rejeu (cf. `useRejeuAuSurvol`).
//
// Fichiers frères à garder synchrones : public/favicon.svg, public/favicon-shield.svg,
// public/logo.svg, public/og-image.png. Ils sont STATIQUES et le restent : un favicon ou
// une og:image animés n'ont pas de sens (et ne sont pas rendus par ce composant).
import { useEffect, useRef } from 'react'

// Le réglage « animations réduites » est lu ici ET par le retour en haut de page
// (components/LienAccueil.tsx) : il vit dans son propre module pour que la requête média
// ne soit écrite qu'une fois. Voir animationsReduites.ts.
import { useAnimationsReduites } from './animationsReduites'
import '@fontsource/ibm-plex-sans/600.css'

/** L'encre de la marque. Volontairement distincte de `--color-brand-900` (#102a43), qui
 *  est l'encre de l'INTERFACE : la décision du 03/08 nomme #0D1F3C et rien d'autre. Le
 *  jeton vit dans index.css (`--color-marque-marine`) pour que les deux ne dérivent pas. */
export const MARQUE_MARINE = '#0D1F3C'

/** Corps du mot, en **rem** — jamais en px. Le lockup remplaçait `text-lg/xl/2xl`, qui
 *  sont des rem : une taille en px aurait figé la marque pendant que tout le reste de la
 *  page grossit chez qui a monté la taille de police par défaut de son navigateur
 *  (WCAG 1.4.4). Les valeurs sont celles des classes remplacées : 1,125 / 1,25 / 1,5 rem,
 *  soit 18 / 20 / 24 px au réglage par défaut. Le symbole suit, il est en em. */
const TAILLE_MOT = { sm: 1.125, md: 1.25, lg: 1.5 } as const

/**
 * Rejoue la vague au survol et au focus — le seul mouvement qui reste après le montage.
 *
 * Pourquoi en JS et pas une règle `:hover` : une animation accrochée à `:hover` est
 * DÉTACHÉE dès que le pointeur s'en va, et la barre retombe d'un coup au milieu de sa
 * course. Ici on relance l'animation déjà déclarée dans la feuille (`getAnimations()` →
 * `cancel()` + `play()`), donc la vague part et va au bout, pointeur ou pas. Bonus : les
 * images-clés ne sont pas dupliquées en JS, elles restent la seule source de vérité dans
 * index.css — y compris les décalages, qui se rejouent à l'identique.
 *
 * Le FOCUS ne s'écoute pas sur le lockup : celui-ci n'est pas focusable, et `focusin`
 * remonte, il ne descend pas. Dans l'en-tête, le pied de page et les pages de connexion,
 * c'est le LIEN qui enveloppe le lockup qui reçoit le focus — d'où le `closest('a, button')`.
 *
 * Aucun minuteur : rien ne tourne tant que l'utilisateur ne fait rien, donc rien à
 * suspendre quand l'onglet passe en arrière-plan.
 */
function useRejeuAuSurvol(actif: boolean, racine: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!actif) return
    const lockup = racine.current
    if (!lockup) return

    const barres = Array.from(lockup.querySelectorAll('[class^="logo-barre-"]'))
    // `getAnimations` manque sous jsdom (donc dans les tests) et sur les moteurs anciens :
    // sans lui, il n'y a simplement pas de rejeu, et la vague de montage reste acquise.
    if (barres.length === 0 || typeof barres[0].getAnimations !== 'function') return

    const rejouer = () => {
      for (const barre of barres) {
        for (const animation of barre.getAnimations()) {
          // `cancel()` puis `play()` : `play()` seul ne relance qu'une animation TERMINÉE,
          // et ne ferait rien si l'on survole pendant que la vague court encore.
          animation.cancel()
          animation.play()
        }
      }
    }

    const cibleFocus = lockup.closest('a, button') ?? lockup
    lockup.addEventListener('pointerenter', rejouer)
    cibleFocus.addEventListener('focusin', rejouer)
    return () => {
      lockup.removeEventListener('pointerenter', rejouer)
      cibleFocus.removeEventListener('focusin', rejouer)
    }
  }, [actif, racine])
}

export default function Logo({
  size = 'md',
  variant = 'marine',
  animation = true,
}: {
  size?: 'sm' | 'md' | 'lg'
  variant?: 'marine' | 'inverse'
  /**
   * Laisser les trois barres respirer. `false` fige la marque dans son état de référence.
   *
   * À mettre à `false` partout où le logo n'est pas regardé mais REPRODUIT : la couverture
   * des PDF clients (obs/PrintView), et toute future capture. Le CSS pose déjà un
   * `@media print`, mais le PDF est assemblé par un Chromium HEADLESS piloté par le
   * backend, et cette prop est la seule garde qui ne dépende pas de la façon dont ce
   * Chromium-là décide d'appliquer ses media queries. Les deux gardes sont volontairement
   * redondantes : un livrable client avec une barre figée à 78 % de sa hauteur serait un
   * défaut invisible en revue et permanent chez le client.
   */
  animation?: boolean
}) {
  const ink = variant === 'inverse' ? 'text-white' : 'text-marque-marine'
  // Le hook est appelé AVANT d'être combiné à la prop, et jamais dans un `&&` : un
  // `animation && !useAnimationsReduites()` court-circuiterait l'appel dès que la prop est
  // à `false`, et le nombre de hooks changerait d'un rendu à l'autre (règle des hooks).
  const animationsReduites = useAnimationsReduites()
  // Trois conditions, toutes nécessaires : la prop, le réglage système, et — dans le CSS —
  // le contexte d'impression.
  const anime = animation && !animationsReduites
  const racine = useRef<HTMLSpanElement>(null)
  useRejeuAuSurvol(anime, racine)

  return (
    <span
      ref={racine}
      className={`inline-flex items-center font-plex ${ink}`}
      style={{ fontSize: `${TAILLE_MOT[size]}rem`, gap: '0.2547em' }}
      role="img"
      aria-label="TerObs"
    >
      {/* `flex-shrink-0` : dans la topbar de l'observatoire le lockup voisine un nom de
          commune qui peut être long — sans ça le symbole s'écrase avant le texte. */}
      <span className="flex-shrink-0" style={{ width: '0.9685em', height: '0.8473em' }}>
        {/* `currentColor` partout — voir la règle 3. `stroke-linejoin="round"` : sans lui
            les six sommets produisent des pointes qui se détachent en pixels isolés sous
            24 px (et le viewBox resserré ci-dessus suppose des joints ronds : un joint
            en onglet déborderait du cadre aux deux sommets latéraux). */}
        <svg
          viewBox="4.8 7.2 38.4 33.6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`w-full h-full${anime ? ` logo-anime${variant === 'inverse' ? ' logo-anime-inverse' : ''}` : ''}`}
          aria-hidden="true"
        >
          <path
            d="M42 24 L33 39.6 L15 39.6 L6 24 L15 8.4 L33 8.4 Z"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinejoin="round"
          />
          {/* Les trois barres : même pied (y = 34), hauteurs croissantes.
              Le CONTOUR n'est jamais animé — c'est lui qui dit « territoire », et un
              hexagone qui bouge ferait bouger la marque entière. Seules les barres, qui
              disent la donnée, respirent.
              Les classes `logo-barre-*` sont posées en permanence : elles ne sont que des
              crochets de sélection. C'est `logo-anime` sur le <svg> ci-dessus qui décide
              si quelque chose bouge — donc le dessin au repos est strictement celui du
              03/08, sans classe conditionnelle à relire barre par barre. */}
          <rect className="logo-barre-1" x="15.6" y="28.5" width="4.2" height="5.5" rx="1.3" fill="currentColor" />
          <rect className="logo-barre-2" x="21.9" y="24" width="4.2" height="10" rx="1.3" fill="currentColor" />
          <rect className="logo-barre-3" x="28.2" y="19.5" width="4.2" height="14.5" rx="1.3" fill="currentColor" />
        </svg>
      </span>
      {/* Pas de `tracking-*` : la chasse mesurée sur la planche (91,9 pt d'encre pour un
          corps de 28,5) est celle d'IBM Plex Sans 600 SANS interlettrage. En ajouter ici
          ferait diverger le lockup de public/logo.svg, dont le mot est vectorisé. */}
      <span className="font-semibold leading-none">TerObs</span>
    </span>
  )
}
